/**
 * Centralized Color Constants for FleetPulse
 * 
 * USE THESE instead of hardcoding hex values in components!
 * This enables consistent theming and future light/dark mode support.
 */

// ============================================
// PRIMARY BRAND COLORS
// ============================================
export const PRIMARY = '#3b82f6';           // Action blue — used only for interactive elements
export const PRIMARY_LIGHT = '#60a5fa';     // Lighter variant
export const PRIMARY_DARK = '#2563eb';      // Darker variant

// Kept as names for compatibility; flat fills read cleaner on data screens than gradients
export const PRIMARY_GRADIENT = PRIMARY;
export const PRIMARY_GRADIENT_HOVER = PRIMARY_DARK;

// ============================================
// STATUS COLORS (Semantic)
// ============================================
export const SUCCESS = '#10b981';           // Green - completed, good, active
export const SUCCESS_LIGHT = '#34d399';     // Lighter green
export const SUCCESS_DARK = '#059669';      // Darker green
export const SUCCESS_BG = 'rgba(16, 185, 129, 0.15)';
export const SUCCESS_GRADIENT = 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)';
export const SUCCESS_GRADIENT_HOVER = 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)';

export const WARNING = '#f59e0b';           // Amber/Orange - due soon, attention
export const WARNING_LIGHT = '#fbbf24';     // Lighter amber
export const WARNING_DARK = '#d97706';      // Darker amber
export const WARNING_BG = 'rgba(245, 158, 11, 0.15)';
export const WARNING_GRADIENT = 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)';
export const WARNING_GRADIENT_HOVER = 'linear-gradient(135deg, #f57c00 0%, #e64a19 100%)';

export const ERROR = '#ef4444';             // Red - overdue, error, delete
export const ERROR_LIGHT = '#f87171';       // Lighter red
export const ERROR_DARK = '#dc2626';        // Darker red
export const ERROR_BG = 'rgba(239, 68, 68, 0.15)';

export const INFO = '#38bdf8';              // Sky - info, upcoming (distinct from action blue)
export const INFO_LIGHT = '#7dd3fc';        // Lighter sky
export const INFO_DARK = '#0ea5e9';         // Darker sky
export const INFO_BG = 'rgba(56, 189, 248, 0.15)';

export const MUTED = '#6b7280';             // Gray - unknown, inactive
export const MUTED_BG = 'rgba(107, 114, 128, 0.15)';

// Additional status background aliases for convenience
export const STATUS_BG_ERROR = ERROR_BG;
export const STATUS_BG_WARNING = WARNING_BG;
export const STATUS_BG_SUCCESS = SUCCESS_BG;
export const STATUS_BG_INFO = INFO_BG;
export const STATUS_BG_MUTED = MUTED_BG;

// ============================================
// BACKGROUND COLORS
// ============================================
export const BG_DEFAULT = '#0b1320';        // Main graphite background
export const BG_PAPER = '#111d2d';          // Card/paper background
export const BG_ELEVATED = '#17283a';       // Elevated surface

// Glass effect backgrounds
export const GLASS_BG = 'rgba(255, 255, 255, 0.03)';
export const GLASS_BG_HOVER = 'rgba(255, 255, 255, 0.05)';
export const GLASS_BORDER = 'rgba(255, 255, 255, 0.08)';
export const GLASS_BORDER_HOVER = 'rgba(255, 255, 255, 0.12)';

// ============================================
// TEXT COLORS
// ============================================
export const TEXT_PRIMARY = '#ffffff';
export const TEXT_SECONDARY = 'rgba(255, 255, 255, 0.7)';
export const TEXT_MUTED = 'rgba(255, 255, 255, 0.5)';
export const TEXT_DISABLED = 'rgba(255, 255, 255, 0.3)';

// ============================================
// ACCENT COLORS (for variety in charts, badges)
// ============================================
export const ACCENT_PURPLE = '#3b82f6';
export const ACCENT_PINK = '#ec4899';
export const ACCENT_CYAN = '#06b6d4';
export const ACCENT_TEAL = '#14b8a6';
export const ACCENT_YELLOW = '#ffd93d';

// Secondary emphasis: flat, same family as primary (pink gradients retired)
export const SECONDARY_GRADIENT = PRIMARY_LIGHT;
export const SECONDARY_GRADIENT_HOVER = PRIMARY;

// ============================================
// COMPLIANCE/CATEGORY SPECIFIC COLORS
// ============================================
export const COMPLIANCE = {
  inspection: '#4facfe',
  registration: '#43e97b', 
  tax: '#fa709a',
  insurance: '#3b82f6',
};

export const MAINTENANCE = {
  oil: '#fbbf24',
  tires: '#3b82f6',
  brakes: '#ef4444',
  battery: '#10b981',
  filter: '#06b6d4',
  general: '#3b82f6',
};

// ============================================
// HELPER: Status color getter
// ============================================
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'overdue':
    case 'error':
    case 'failed':
      return ERROR;
    case 'due_soon':
    case 'warning':
    case 'pending':
      return WARNING;
    case 'good':
    case 'completed':
    case 'success':
    case 'active':
      return SUCCESS;
    case 'upcoming':
    case 'info':
    case 'scheduled':
      return INFO;
    default:
      return TEXT_MUTED;
  }
};

export const getStatusBg = (status) => {
  switch (status?.toLowerCase()) {
    case 'overdue':
    case 'error':
    case 'failed':
      return ERROR_BG;
    case 'due_soon':
    case 'warning':
    case 'pending':
      return WARNING_BG;
    case 'good':
    case 'completed':
    case 'success':
    case 'active':
      return SUCCESS_BG;
    case 'upcoming':
    case 'info':
    case 'scheduled':
      return INFO_BG;
    default:
      return GLASS_BG;
  }
};

// ============================================
// COMPONENT STYLE HELPERS
// ============================================
export const glassCardStyle = {
  background: GLASS_BG,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${GLASS_BORDER}`,
  borderRadius: 2,
};

export const primaryButtonStyle = {
  background: PRIMARY_GRADIENT,
  color: TEXT_PRIMARY,
  '&:hover': {
    background: PRIMARY_GRADIENT_HOVER,
    transform: 'translateY(-2px)',
    boxShadow: 3,
  },
  transition: 'all 0.2s',
};

export const statusChipStyle = (status) => ({
  background: getStatusBg(status),
  color: getStatusColor(status),
  fontWeight: 600,
  border: `1px solid ${getStatusColor(status)}40`,
});
